import { DefaultValue as RecoilDefaultValue, selector, } from 'recoil';
/**
 * Create a helper to get fields of an object as recoil selectors
 * @example
 *  const helper = createFieldSelector(baseSelector);
 *  const fieldABCSelector = helper('abc');
 * @param baseSelector selector or atom of an object with fields
 * @returns function
 */
export const createFieldSelector = (baseSelector) => 
/**
 * Pick a field by fieldName, provide options for defaultValue
 * Create a *selector* that picks a field from baseSelector:
 *  - **Gets** baseSelector[fieldName]
 *  - **Sets** baseSelector[fieldName]
 * @example
 *  {a: 'b', c: 'd'} => selector('c') => 'd'
 * @param fieldName name of key in object
 * @param options.defaultValue required defaultValue for created selector
 * @returns selector
 */
(fieldName, options, keySuffix) => {
    if (!baseSelector) {
        throw new Error('Please define a valid baseSelector param to use createBaseSelector');
    }
    const key = `${baseSelector.key}/${String(fieldName)}${keySuffix ? `/${keySuffix}` : ''}`;
    return selector({
        key,
        get: ({ get }) => {
            const base = get(baseSelector);
            // TODO: this could probably type better
            const field = ((base && base[fieldName]) ??
                options.defaultValue);
            return field;
        },
        set: ({ set }, value) => {
            set(baseSelector, (prev) => ({
                ...prev,
                [fieldName]: value instanceof RecoilDefaultValue ? options.defaultValue : value,
            }));
        },
    });
};
/**
 * Create a *selector* that allows you to use an array as if it was a single element:
 *  - **Gets** any element from the array
 *  - **Sets** the element provided to *all* elements of the array
 * @example
 *  [{a: 'c'}, {a: 'b'}, {a: 'c'}] => selector => {a: 'c'}
 *  selector({a : 'a'}) => [{a: 'a'}, {a: 'a'}, {a: 'a'}]
 * @param baseSelector selector or atom of an array
 * @param options.defaultValue required defaultValue for created selector
 * @returns selector
 */
export const createArrayAnySelector = (baseSelector, options) => selector({
    key: `${baseSelector.key}/arrayAny`,
    get: ({ get }) => {
        const arr = get(baseSelector);
        return Array.isArray(arr)
            ? (arr[0] ?? options.defaultValue)
            : options.defaultValue;
    },
    set: ({ set }, value) => {
        // set in all array elements
        set(baseSelector, (prevItems) => value instanceof RecoilDefaultValue
            ? []
            : prevItems?.length === 0
                ? [value]
                : prevItems.map(() => value));
        return;
    },
});
/**
 * Create a *selectorFamily* that:
 *  - **Gets** field from any object of an array
 *  - **Sets** that field in all objects of the array (without changing other fields in object)
 * @example
 *  [{fieldName: 'value', type: 'b'}, {fieldName: 'value', type: 'a'}] => selector() => 'value'
 * @param anySelector used as getter to get any element (e.g. selector from createArrayAnySelector(x))
 * @param allSelector used as setter to write into all elements (e.g. selector "x" used in any selector)
 * @param fieldName name of key in object
 * @param options.defaultValue required defaultValue for created selector
 * @returns selector
 */
export const createArrayAnyFieldSelector = (anySelector, allSelector, fieldName, options) => selector({
    key: `${anySelector.key}/${allSelector.key}/${String(fieldName)}/arrayAnyField`,
    get: ({ get }) => {
        const any = get(anySelector);
        return (any && any[fieldName]) ?? options.defaultValue;
    },
    set: ({ set }, value) => {
        // merge in all array elements
        const update = value instanceof RecoilDefaultValue
            ? { [fieldName]: options.defaultValue }
            : { [fieldName]: value };
        set(allSelector, (prev) => prev.map((e) => ({
            ...e,
            ...update,
        })));
    },
});
